import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { itemsCol } from '../shared/array-types';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { publishAuthoringFields, validatePrivateItemPublish, type PublishData } from '../janus-engine/authoring/publish-validation';

const pending = new Set<string>();
const baseUrl = () => typeof BASE_URL === 'string' ? BASE_URL : '/';
function publishData(): PublishData {
    const data: unknown = JSON.parse(document.getElementById('ft-private-publish-data')?.textContent ?? 'null');
    if (!fromBaseRecord(data) || !fromBaseRecord(data.catalog) || !fromBaseRecord(data.rules) || !Array.isArray(data.rules.rules)
        || !fromBaseRecord(data.descent) || !Array.isArray(data.objectTypes) || !Array.isArray(data.aoeTypes))
        throw new Error('Authoring data unavailable. Please retry.');
    return data as unknown as PublishData;
}

/** Always fetch the saved definition; unsupported object-route responses cannot bypass validation. */
export async function privateItemPublishFindings(id: string) {
    const response = await fetch(`${baseUrl()}options/api/authored_card_inputs_inc.php?id=${encodeURIComponent(id)}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Item inputs unavailable (${response.status}). Please retry.`);
    const source: unknown = await response.json();
    if (!fromBaseRecord(source) || !fromBaseRecord(source._entity_base) || source._entity_base.entity !== 'definition'
        || String(source._entity_base.entity_id).toUpperCase() !== id.toUpperCase() || !fromBaseRecord(source._entity_base.base))
        throw new Error('Current item definition unavailable. Please retry.');
    const direct = { ...source._entity_base.base }, compiled = await compileFetchedEntity(source);
    if (String(itemsCol(compiled, 'item_id') ?? '').toUpperCase() !== id.toUpperCase()
        || compiled._entity_refusal) throw new Error('Item compilation incomplete. Please retry.');
    const facts = publishData();
    return validatePrivateItemPublish(publishAuthoringFields(direct, compiled, facts), facts);
}

function setPublishState(button: HTMLButtonElement, published: boolean): void {
    button.dataset.published = published ? '1' : '0';
    button.classList.toggle('btn-success', !published);
    button.classList.toggle('btn-outline-secondary', published);
    button.textContent = published ? '✓ Published · undo' : 'Publish ▸';
    button.closest('.pir-card')?.classList.toggle('published', published);
    const row = button.closest('tr');
    if (row) row.style.opacity = published ? '0.5' : '';
}

/** One normal button command; Unpublish does not depend on authoring inputs or compilation. */
export async function privateItemPublishCommand(button: HTMLButtonElement): Promise<void> {
    const id = button.dataset.item ?? '', key = id.toUpperCase();
    if (!id || pending.has(key)) return;
    const current = () => button.isConnected && button.dataset.item === id;
    const unpublish = button.dataset.published === '1', previous = button.textContent;
    pending.add(key);
    button.disabled = true;
    button.textContent = unpublish ? 'Reverting…' : 'Checking…';
    try {
        const findings = unpublish ? { errors: [], warnings: [] } : await privateItemPublishFindings(id);
        if (!current()) return;
        if (findings.errors.length) throw new Error('Publish blocked by authoring rules: ' + findings.errors.slice(0, 5).join(' | '));
        button.textContent = unpublish ? 'Reverting…' : 'Publishing…';
        const response = await fetch(`${baseUrl()}options/tools/items/publish_private_item_inc.php`, { method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ item_id: id, ...(unpublish ? { unpublish: '1' } : {}) }) });
        const result: unknown = await response.json();
        if (!response.ok || !fromBaseRecord(result) || result.ok !== true) throw new Error(fromBaseRecord(result) ? String(result.error ?? 'Unknown error') : 'Bad response');
        if (String(result.item_id).toUpperCase() !== key) throw new Error('Publish response belongs to another item. Please retry.');
        if (!current()) return;
        for (const peer of document.querySelectorAll<HTMLButtonElement>('.ft-publish-btn')) {
            if (peer.dataset.item?.toUpperCase() === key) setPublishState(peer, result.published === true);
        }
        if (result.published === true && findings.warnings.length) window.alert('Published with ' + findings.warnings.length + ' validation finding(s):\n\n'
            + findings.warnings.slice(0, 8).join('\n') + (findings.warnings.length > 8 ? '\n…' : ''));
    } catch (error) {
        if (current()) {
            button.textContent = previous;
            window.alert(`${unpublish ? 'Revert' : 'Publish'} failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    } finally {
        pending.delete(key);
        if (current()) button.disabled = false;
    }
}

document.addEventListener('click', event => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('.ft-publish-btn') : null;
    if (button && document.getElementById('ft-private-publish-data')) void privateItemPublishCommand(button);
});
